import { Collapse as BlueprintCollapse } from '@blueprintjs/core';
import { ChatLaunch } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { VoiceboxChatDocument } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatDocument';
import { VoiceboxChatGenerated } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatGenerated';
import { VoiceboxChatIncoming } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatIncoming';
import { VoiceboxChatMessageResponseActions } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatMessageResponseActions';
import { VoiceboxChatReasoning } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatReasoning';
import { VoiceboxChatUnverified } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatUnverified';
import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useParsedChartSpecFromMessage } from 'bones/components/molecules/Voicebox/hooks/useParsedChartSpecFromMessage';
import { useParsedCsvFromMessage } from 'bones/components/molecules/Voicebox/hooks/useParsedCsvFromMessage';
import { useReasoningStepsFromMessage } from 'bones/components/molecules/Voicebox/hooks/useReasoningStepsFromMessage';
import { VoiceboxCsvTable } from 'bones/components/molecules/Voicebox/shared/VoiceboxCsvTable';
import { VoiceboxMarkdown } from 'bones/components/molecules/Voicebox/shared/VoiceboxMarkdown';
import { VoiceboxQueryBuilder } from 'bones/components/molecules/Voicebox/shared/VoiceboxQueryBuilder';
import {
  VoiceboxResponseActionType,
  VoiceboxResponseMessage,
} from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Button } from 'bones/design-system/components/Button';
import { Chart } from 'bones/design-system/components/Chart';
import { LoadingIcon } from 'bones/design-system/components/icons/LoadingIcon';
import { SystemMessage } from 'bones/design-system/components/notifications/SystemMessage';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';

export interface VoiceboxChatMessageResponseProps {
  isReasoningOpen: boolean;
  message: VoiceboxResponseMessage;
  messageIdx: number;
  messagesRef: React.RefObject<HTMLDivElement>;
  selected?: boolean;
  sendMessage: (message: string) => void;
  sendMessageDisabled: boolean;
  setIsReasoningOpen: (isOpen: boolean) => void;
  settings: VoiceboxSettings;
}

export const VoiceboxChatMessageResponse: React.VFC<
  VoiceboxChatMessageResponseProps
> = ({
  isReasoningOpen,
  message,
  messageIdx,
  messagesRef,
  selected,
  sendMessage,
  sendMessageDisabled,
  setIsReasoningOpen,
  settings,
}) => {
  const isMounted = useIsMounted();
  const { copy, testIds, links } = useVoiceboxSchema();
  const { messages } = useVoiceboxConversation();
  const { setPanelHistory } = useVoiceboxPanelData();

  const isLastMessage = messageIdx === messages.length - 1;

  const [isChartShown, setIsChartShown] = React.useState(true);
  const [isQueryBuilderShown, setIsQueryBuilderShown] = React.useState(false);
  const [isTableShown, setIsTableShown] = React.useState(false);
  const [isUnverifiedShown, setIsUnverifiedShown] = React.useState(false);
  const [isUnverifiedLoading, setIsUnverifiedLoading] = React.useState(false);
  const [hasValidChart, setHasValidChart] = React.useState<boolean | undefined>(
    undefined
  );

  const externalLlmAction = message.actions?.find(
    (action) => action.type === VoiceboxResponseActionType.EXTERNAL_LLM
  );

  const llmAction = message.actions?.find(
    (action) => action.type === VoiceboxResponseActionType.LLM
  );

  const reasoningAction = message.actions?.find(
    (action) => action.type === VoiceboxResponseActionType.REASONING
  );

  const safetyRagAction = message.actions?.find(
    (action) => action.type === VoiceboxResponseActionType.SAFETY_RAG
  );

  const sparqlQueryActions = message.actions?.filter(
    (action) => action.type === VoiceboxResponseActionType.SPARQL
  );

  const parsedCsv = useParsedCsvFromMessage(message);

  const { error: chartError, spec: chartSpec } =
    useParsedChartSpecFromMessage(message);

  const reasoningSteps = useReasoningStepsFromMessage({
    action: reasoningAction,
    message,
  });

  const actionsRef = React.useRef<HTMLDivElement>(null);

  const handleSetIsChartShown = (nextIsShown: boolean) => {
    setIsChartShown(nextIsShown);
    if (nextIsShown) {
      setTimeout(() => {
        // istanbul ignore else
        if (actionsRef.current) {
          actionsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 300);
    }
  };

  const handleSetIsQueryBuilderShown = (nextIsShown: boolean) => {
    setIsQueryBuilderShown(nextIsShown);
    if (nextIsShown) {
      setTimeout(() => {
        // istanbul ignore else
        if (actionsRef.current) {
          actionsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 300);
    }
  };

  const handleSetIsTableShown = (nextIsShown: boolean) => {
    setIsTableShown(nextIsShown);
    if (nextIsShown) {
      setTimeout(() => {
        // istanbul ignore else
        if (actionsRef.current) {
          actionsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 300);
    }
  };

  let badge: JSX.Element | null = null;
  if (externalLlmAction && isUnverifiedShown) {
    badge = <VoiceboxChatUnverified action={externalLlmAction} />;
  } else if (llmAction) {
    badge = <VoiceboxChatGenerated action={llmAction} />;
  } else if (safetyRagAction) {
    badge = <VoiceboxChatDocument action={safetyRagAction} />;
  }

  return (
    <>
      <VoiceboxChatIncoming
        actions={
          <VoiceboxChatMessageResponseActions
            hasChart={Boolean(hasValidChart)}
            hasTable={Boolean(parsedCsv)}
            isChartShown={isChartShown}
            isQueryBuilderShown={isQueryBuilderShown}
            isTableShown={isTableShown}
            message={message}
            messageIdx={messageIdx}
            messagesRef={messagesRef}
            sendMessage={sendMessage}
            sendMessageDisabled={sendMessageDisabled}
            setIsChartShown={handleSetIsChartShown}
            setIsQueryBuilderShown={handleSetIsQueryBuilderShown}
            setIsTableShown={handleSetIsTableShown}
            settings={settings}
          />
        }
        actionsRef={actionsRef}
        avatarTestId={testIds.messages.avatar.voiceboxLogo}
        badge={badge}
        content={
          <div
            style={{
              flexDirection: 'column',
              display: 'flex',
              gap: '10px',
            }}
          >
            {reasoningSteps.length ? (
              <VoiceboxChatReasoning
                isOpen={isReasoningOpen}
                message={message}
                reasoningSteps={reasoningSteps}
                setIsOpen={setIsReasoningOpen}
                settings={settings}
              />
            ) : undefined}

            {externalLlmAction && !isUnverifiedShown ? (
              copy.message.unverified.message
            ) : (
              <VoiceboxMarkdown
                linkTestId={testIds.messages.responseLink}
                markdown={message.result}
                messageId={message.messageId}
                settings={settings}
              />
            )}
          </div>
        }
        onSelect={
          links.openLinksInPanel
            ? () => {
                setPanelHistory(
                  selected ? [] : [{ messageId: message.messageId }]
                );
              }
            : undefined
        }
        selectable={links.openLinksInPanel && message.success}
        selected={selected}
        showActions={isLastMessage}
        slot={
          <>
            {chartError || hasValidChart === false ? (
              <SystemMessage
                testId={testIds.messages.chart.error}
                text={copy.message.menu.chart.chartError}
                variant="alert"
              />
            ) : null}
            {externalLlmAction && !isUnverifiedShown ? (
              <Button
                className="vb-voicebox-chat-unverified-button"
                leftIcon={isUnverifiedLoading ? LoadingIcon : ChatLaunch}
                onClick={
                  isUnverifiedLoading
                    ? undefined
                    : () => {
                        setIsUnverifiedLoading(true);
                        setTimeout(() => {
                          // istanbul ignore else
                          if (isMounted.current) {
                            setIsUnverifiedLoading(false);
                            setIsUnverifiedShown(true);
                          }
                        }, 3000);
                      }
                }
                size="sm"
                testId={testIds.messages.responseUnverified(
                  message.messageId,
                  messageIdx
                )}
                variant="ghost"
              >
                {copy.message.unverified.button}
              </Button>
            ) : null}
          </>
        }
        testId={testIds.messages.responseMessage(message.messageId, messageIdx)}
      />
      {parsedCsv ? (
        <BlueprintCollapse isOpen={isTableShown}>
          <div
            className={classNames('vb-voicebox-chat-indicator', {
              'vb-voicebox-chat-indicator--selected': selected,
            })}
          >
            <VoiceboxCsvTable
              message={message}
              messageIdx={messageIdx}
              parsed={parsedCsv}
              settings={settings}
              showDownload
            />
          </div>
        </BlueprintCollapse>
      ) : null}
      {chartSpec ? (
        <BlueprintCollapse isOpen={isChartShown}>
          <div
            className={classNames('vb-voicebox-chat-indicator', {
              'vb-voicebox-chat-indicator--selected': selected,
            })}
          >
            <Chart
              onValidateChart={setHasValidChart}
              spec={chartSpec}
              testId={testIds.messages.chart.container}
            />
          </div>
        </BlueprintCollapse>
      ) : null}
      {sparqlQueryActions?.map((action) => {
        return (
          <BlueprintCollapse
            key={`${
              message.messageId || /* istanbul ignore next */ messageIdx
            }-${action.value}`}
            isOpen={isQueryBuilderShown}
          >
            <div
              className={classNames('vb-voicebox-chat-indicator', {
                'vb-voicebox-chat-indicator--selected': selected,
              })}
            >
              <VoiceboxQueryBuilder
                key={action.value}
                messagesRef={messagesRef}
                query={action.value}
                seeQueryTestId={testIds.messages.responseQueryBuilderSeeQuery(
                  message.messageId,
                  messageIdx
                )}
                settings={settings}
              />
            </div>
          </BlueprintCollapse>
        );
      })}
      {message.examples?.length ? (
        <div
          className={classNames(
            'vb-voicebox-chat-examples',
            'vb-voicebox-chat-indicator',
            {
              'vb-voicebox-chat-indicator--selected': selected,
            }
          )}
        >
          {message.examples.map((example, exampleIdx) => (
            <Button
              key={`${
                message.messageId || /* istanbul ignore next */ messageIdx
              }-${example}`}
              className="vb-voicebox-chat-example-button"
              onClick={
                sendMessageDisabled ? undefined : () => sendMessage(example)
              }
              size="sm"
              testId={testIds.messages.responseExample(example, exampleIdx)}
              variant="tertiary"
            >
              {example}
            </Button>
          ))}
        </div>
      ) : null}
    </>
  );
};
